import React, { useState, useEffect, useCallback } from 'react';
import { Truck, ChevronDown, ChevronUp, Save, ClipboardList, Shield } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { API } from '@/utils/api';
import axios from 'axios';
import { toast } from 'sonner';

const BASIC_ITEMS = [
  { key: 'nettoyage_exterieur', label: 'Nettoyage exterieur', options: ['A faire', 'Fait'] },
  { key: 'nettoyage_interieur', label: 'Nettoyage interieur', options: ['A faire', 'Fait'] },
  { key: 'nettoyage_moteur', label: 'Nettoyage moteur', options: ['A faire', 'A controler', 'Fait'] },
  { key: 'vitres', label: 'Vitres', options: ['A faire', 'Fait'] },
  { key: 'horloge_vehicule', label: 'Horloge vehicule', options: ['A regler', 'Fait'] },
  { key: 'temoins_tableau_bord', label: 'Temoins tableau de bord', options: ['A controler', 'Fait'] },
];

const ROUE_ITEMS = [
  { key: 'roues_ete_monter', label: 'Roues ete - A monter', options: ['A faire', 'Fait'] },
  { key: 'roues_ete_coffre', label: 'Roues ete - Dans le coffre', options: ['A faire', 'Fait'] },
  { key: 'roues_hiver_monter', label: 'Roues hiver - A monter', options: ['A faire', 'Fait'] },
  { key: 'roues_hiver_coffre', label: 'Roues hiver - Dans le coffre', options: ['A faire', 'Fait'] },
];

const CG_ITEMS = [
  { key: 'cg_carrosserie', label: 'Etat general carrosserie' },
  { key: 'cg_pneus', label: 'Etat pneus' },
  { key: 'cg_parechocs', label: 'Etat pare-chocs' },
  { key: 'cg_essai_routier', label: 'Essai routier' },
  { key: 'cg_bruits_moteur', label: 'Bruits moteur' },
  { key: 'cg_defauts', label: 'Paquet de defauts' },
  { key: 'cg_eclairage', label: 'Eclairage' },
  { key: 'cg_freins', label: 'Freins (disques, plaquettes)' },
  { key: 'cg_direction', label: 'Direction' },
  { key: 'cg_suspension', label: 'Suspension' },
  { key: 'cg_echappement', label: 'Echappement' },
  { key: 'cg_niveaux', label: 'Niveaux (huile, liquides)' },
  { key: 'cg_climatisation', label: 'Climatisation' },
  { key: 'cg_vitres_retro', label: 'Vitres / retroviseurs' },
  { key: 'cg_ceintures', label: 'Ceintures securite' },
];

const ALL_KEYS = [...BASIC_ITEMS.map(i => i.key), ...ROUE_ITEMS.map(i => i.key), 'controle_global', ...CG_ITEMS.map(i => i.key)];

function getStatusStyle(val) {
  if (!val) return 'bg-slate-50 border-slate-200';
  if (val === 'Fait') return 'bg-emerald-50 border-emerald-300';
  return 'bg-amber-50 border-amber-300';
}

function getStatusDot(val) {
  if (!val) return 'bg-slate-300';
  if (val === 'Fait') return 'bg-emerald-500';
  return 'bg-amber-500';
}

const DEMANDE_LABELS = {
  service_standard: 'Service standard',
  grand_service: 'Grand service',
  diagnostic: 'Diagnostic',
  changement_roues: 'Changement de roues',
  montage_pneus: 'Montage pneus',
  controle_expertise: 'Controle avant expertise',
  preparation_expertise: 'Preparation expertise',
  presentation_expertise: 'Presentation expertise',
  tolerie: 'Tolerie',
  peinture: 'Peinture',
  remplacement_pare_brise: 'Remplacement pare-brise',
  infiltration_eau: "Infiltration d'eau",
  cas_assurance: 'Cas assurance',
  lavage_moteur: 'Lavage moteur',
  lavage_chassis: 'Lavage chassis',
};

const DEMANDE_CATEGORIES = {
  mecanique: ['service_standard', 'grand_service', 'diagnostic', 'changement_roues', 'montage_pneus', 'controle_expertise', 'preparation_expertise', 'presentation_expertise'],
  carrosserie: ['tolerie', 'peinture', 'remplacement_pare_brise', 'infiltration_eau'],
  assurance: ['cas_assurance'],
  speciale: ['lavage_moteur', 'lavage_chassis'],
};

const DemandesClientSection = ({ checklist }) => {
  if (!checklist || typeof checklist !== 'object') return null;
  const activeKeys = Object.keys(checklist).filter(k => checklist[k] === true && DEMANDE_LABELS[k]);
  const hasSinistre = checklist.cas_assurance && (checklist.sinistre_1 || checklist.sinistre_2 || checklist.sinistre_3);
  const hasDemandeSpeciale = checklist.demande_speciale_texte;
  if (activeKeys.length === 0 && !hasSinistre && !hasDemandeSpeciale) return null;

  return (
    <div className="bg-amber-50/50 border border-amber-200 rounded-lg p-3 space-y-2" data-testid="demandes-client-display">
      <div className="flex items-center gap-2">
        <ClipboardList className="w-4 h-4 text-amber-600" />
        <span className="text-xs font-bold text-amber-800 uppercase tracking-wider">Demandes du client</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(DEMANDE_CATEGORIES).map(([cat, keys]) => {
          const active = keys.filter(k => checklist[k] === true);
          if (active.length === 0 && cat !== 'assurance') return null;
          if (cat === 'assurance' && !checklist.cas_assurance) return null;
          const catLabel = cat === 'mecanique' ? 'Mecanique' : cat === 'carrosserie' ? 'Carrosserie' : cat === 'assurance' ? 'Assurance' : 'Demande speciale';
          return (
            <div key={cat}>
              <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">{catLabel}</p>
              <div className="space-y-0.5">
                {active.map(k => (
                  <p key={k} className="text-xs text-slate-700 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                    {DEMANDE_LABELS[k]}
                  </p>
                ))}
              </div>
              {cat === 'assurance' && hasSinistre && (
                <div className="mt-1.5 space-y-1 ml-2.5">
                  {checklist.sinistre_1 && (
                    <div className="flex items-center gap-1.5">
                      <Shield className="w-3 h-3 text-amber-600" />
                      <span className="text-xs font-mono font-medium text-amber-800" data-testid="display-sinistre-1">{checklist.sinistre_1}</span>
                    </div>
                  )}
                  {checklist.sinistre_2 && (
                    <div className="flex items-center gap-1.5">
                      <Shield className="w-3 h-3 text-amber-600" />
                      <span className="text-xs font-mono font-medium text-amber-800" data-testid="display-sinistre-2">{checklist.sinistre_2}</span>
                    </div>
                  )}
                  {checklist.sinistre_3 && (
                    <div className="flex items-center gap-1.5">
                      <Shield className="w-3 h-3 text-amber-600" />
                      <span className="text-xs font-mono font-medium text-amber-800" data-testid="display-sinistre-3">{checklist.sinistre_3}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {hasDemandeSpeciale && (
          <div>
            <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">Autre</p>
            <p className="text-xs text-slate-700 italic" data-testid="display-demande-speciale">{checklist.demande_speciale_texte}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export const PreparationLivraisonSection = ({ woId, workOrder, canEdit = true }) => {
  const [expanded, setExpanded] = useState(true);
  const [showCG, setShowCG] = useState(false);
  const [checklist, setChecklist] = useState({});
  const [notes, setNotes] = useState({});
  const [saving, setSaving] = useState(false);

  const loadChecklist = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/work-orders/${woId}/preparation-checklist`);
      setChecklist(res.data.checklist || {});
      setNotes(res.data.notes || {});
      if (res.data.checklist?.controle_global) setShowCG(true);
    } catch { /* no checklist yet */ }
  }, [woId]);

  useEffect(() => { loadChecklist(); }, [loadChecklist]);

  const updateValue = async (key, value) => {
    if (!canEdit) return;
    const updated = { ...checklist, [key]: value };
    setChecklist(updated);
    try {
      await axios.put(`${API}/work-orders/${woId}/preparation-checklist`, { checklist: updated, notes });
    } catch { toast.error('Erreur sauvegarde'); }
  };

  const updateNote = (key, noteText) => {
    setNotes(prev => ({ ...prev, [key]: noteText }));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/work-orders/${woId}/preparation-checklist`, { checklist, notes });
      toast.success('Fiche sauvegardee');
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const filledCount = ALL_KEYS.filter(k => checklist[k]).length;
  const totalCount = ALL_KEYS.filter(k => checklist[k] || k.startsWith('cg_') && checklist['controle_global']).length || ALL_KEYS.length;
  const doneCount = ALL_KEYS.filter(k => checklist[k] === 'Fait' || checklist[k] === 'OK').length;

  const exOwner = workOrder?.ex_owner_name || '';
  const newClient = workOrder?.client_name || workOrder?.new_client_name || '';
  const vehicleInfo = [workOrder?.vehicle_brand, workOrder?.vehicle_model].filter(Boolean).join(' ');
  const plateNumber = workOrder?.vehicle_plate || '';

  const renderItem = (item) => {
    const value = checklist[item.key] || '';
    const note = notes[item.key] || '';
    if (!value && !canEdit) return null;
    return (
      <div key={item.key} className={`rounded-lg border p-2.5 transition-colors ${getStatusStyle(value)}`} data-testid={`livraison-item-${item.key}`}>
        <div className="flex items-center gap-3">
          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${getStatusDot(value)}`} />
          <span className="text-sm font-medium text-slate-800 flex-1">{item.label}</span>
          {canEdit ? (
            <Select value={value || undefined} onValueChange={(v) => updateValue(item.key, v)}>
              <SelectTrigger className="w-[140px] h-8 text-xs bg-white">
                <SelectValue placeholder="..." />
              </SelectTrigger>
              <SelectContent>
                {(item.options || ['A controler', 'OK', 'Fait']).map(opt => (
                  <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            value && <span className={`text-sm font-semibold ${value === 'Fait' ? 'text-emerald-700' : 'text-amber-700'}`}>{value}</span>
          )}
        </div>
        {canEdit && (
          <Input value={note} onChange={e => updateNote(item.key, e.target.value)} onBlur={saveAll}
            placeholder="Remarque..." className="mt-1.5 h-7 text-xs bg-white/70 border-slate-200" />
        )}
        {!canEdit && note && <p className="mt-1 text-xs text-slate-600 italic">{note}</p>}
      </div>
    );
  };

  return (
    <Card className="rounded-xl overflow-hidden border-t-4 border-t-orange-500" data-testid="preparation-livraison-section">
      <button className="w-full flex items-center justify-between p-4 hover:bg-orange-50/30 transition-colors"
        onClick={() => setExpanded(!expanded)} data-testid="toggle-preparation-livraison">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center">
            <Truck className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <span className="font-bold text-slate-900 text-sm block">Fiche Livraison</span>
            <span className="text-xs text-slate-500">{doneCount} termines / {filledCount} items</span>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Vehicle info */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="livraison-vehicle-info">
            <div className="grid grid-cols-3 gap-3">
              {exOwner && (
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Ex-proprietaire</span>
                  <p className="text-sm font-semibold text-slate-800">ex {exOwner}</p>
                </div>
              )}
              <div>
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Nouveau client</span>
                <p className="text-sm font-semibold text-slate-800">{newClient || '-'}</p>
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Vehicule</span>
                <p className="text-sm font-semibold text-slate-800">{vehicleInfo}</p>
                {plateNumber && <p className="text-xs text-slate-500">{plateNumber}</p>}
              </div>
            </div>
          </div>

          {/* Demandes du client */}
          <DemandesClientSection checklist={workOrder?.checklist} />

          {/* Progress */}
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-300 ${doneCount >= filledCount && filledCount > 0 ? 'bg-emerald-500' : 'bg-orange-500'}`}
              style={{ width: `${filledCount > 0 ? Math.round((doneCount / filledCount) * 100) : 0}%` }} />
          </div>

          {/* Basic items */}
          <div className="space-y-2" data-testid="livraison-basic-items">
            {BASIC_ITEMS.map(renderItem)}
          </div>

          {/* Roues */}
          <div className="space-y-2">
            <h5 className="text-xs font-bold text-slate-600 uppercase">Roues</h5>
            {ROUE_ITEMS.map(item => {
              const value = checklist[item.key];
              if (!value && !canEdit) return null;
              return renderItem(item);
            })}
          </div>

          {/* Controle global */}
          {(checklist['controle_global'] || canEdit) && (
            <div className="border border-orange-200 rounded-lg overflow-hidden">
              <button className="w-full flex items-center justify-between px-3 py-2.5 bg-orange-50 hover:bg-orange-100 transition-colors"
                onClick={() => setShowCG(!showCG)}>
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${checklist['controle_global'] ? 'bg-amber-500' : 'bg-slate-300'}`} />
                  <span className="text-sm font-bold text-orange-800">Controle global</span>
                  {checklist['controle_global'] && <span className="text-xs text-orange-600">{checklist['controle_global']}</span>}
                </div>
                {showCG ? <ChevronUp className="w-4 h-4 text-orange-400" /> : <ChevronDown className="w-4 h-4 text-orange-400" />}
              </button>
              {showCG && (
                <div className="p-3 space-y-2">
                  {CG_ITEMS.map(item => {
                    const value = checklist[item.key] || '';
                    const note = notes[item.key] || '';
                    if (!value && !canEdit) return null;
                    return (
                      <div key={item.key} className={`rounded-lg border p-2 transition-colors ${getStatusStyle(value)}`} data-testid={`cg-item-${item.key}`}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusDot(value)}`} />
                          <span className="text-xs font-medium text-slate-700 flex-1">{item.label}</span>
                          {canEdit ? (
                            <Select value={value || undefined} onValueChange={v => updateValue(item.key, v)}>
                              <SelectTrigger className="w-[120px] h-7 text-xs bg-white">
                                <SelectValue placeholder="..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="A controler" className="text-xs">A controler</SelectItem>
                                <SelectItem value="OK" className="text-xs">OK</SelectItem>
                                <SelectItem value="Defaut" className="text-xs">Defaut</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            value && <span className={`text-xs font-semibold ${value === 'OK' ? 'text-emerald-700' : 'text-amber-700'}`}>{value}</span>
                          )}
                        </div>
                        {canEdit && (
                          <Input value={note} onChange={e => updateNote(item.key, e.target.value)} onBlur={saveAll}
                            placeholder="Remarque..." className="mt-1 h-6 text-xs bg-white/70 border-slate-200" />
                        )}
                        {!canEdit && note && <p className="mt-0.5 text-xs text-slate-500 italic">{note}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {canEdit && (
            <Button onClick={saveAll} disabled={saving} variant="outline" className="w-full text-sm" data-testid="livraison-save-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Sauvegarde...' : 'Sauvegarder la fiche'}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};
